import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, FileText, ClipboardList, FileSignature, TrendingUp, AlertCircle, UserCheck, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ClerkStatusList from '@/components/ClerkStatusList';
import ClerkKPIWidget from '@/components/ClerkKPIWidget';
import ClerkTasksWidget from '@/components/ClerkTasksWidget';
import FuelBillingCycleWidget from '@/components/FuelBillingCycleWidget';
import UpcomingLeavesCard from '@/components/UpcomingLeavesCard';
import LiveFleetMap from '@/components/admin/LiveFleetMap';
import WeeklyLeaderboardStrip from '@/components/admin/WeeklyLeaderboardStrip';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DIVISION_LABELS = {
  logistics: 'Logistics',
  spares: 'Spares',
  directors: 'Directors',
  loan_administrators: 'Loan Administrators',
};
const DIVISION_COLORS = {
  logistics: 'bg-blue-500',
  spares: 'bg-emerald-500',
  directors: 'bg-violet-500',
  loan_administrators: 'bg-amber-500',
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [divDialogOpen, setDivDialogOpen] = useState(false);
  const [divForm, setDivForm] = useState({});
  const [divSaving, setDivSaving] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const openEditDivisions = () => {
    setDivForm({ ...(stats?.divisions || {}) });
    setDivDialogOpen(true);
  };

  const saveDivisions = async () => {
    setDivSaving(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(divForm).map(([k, v]) => [k, Number(v) || 0])
      );
      const res = await axios.put(`${API_URL}/admin/divisions`, cleaned);
      setStats((s) => ({ ...s, divisions: res.data.divisions, total_headcount: res.data.total_headcount }));
      toast.success('Headcount updated');
      setDivDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setDivSaving(false);
    }
  };

  const statCards = [
    {
      title: 'Total Owner Drivers',
      value: stats?.total_drivers || 0,
      description: `${stats?.active_drivers || 0} active`,
      icon: Users,
      color: 'bg-blue-500',
      link: '/admin/drivers'
    },
    {
      title: 'Total Employees',
      value: stats?.total_employees || 0,
      description: `${stats?.active_employees || 0} assistants & drivers`,
      icon: UserCheck,
      color: 'bg-teal-500',
      link: '/admin/drivers'
    },
    {
      title: 'Total Statements',
      value: stats?.total_statements || 0,
      description: 'Uploaded statements',
      icon: FileText,
      color: 'bg-green-500',
      link: '/admin/statements'
    },
    {
      title: 'Pending Registrations',
      value: stats?.pending_registrations || 0,
      description: 'Awaiting review',
      icon: ClipboardList,
      color: 'bg-amber-500',
      link: '/admin/registrations'
    },
    {
      title: 'Unsigned Contracts',
      value: stats?.unsigned_contracts || 0,
      description: 'Pending signatures',
      icon: FileSignature,
      color: 'bg-purple-500',
      link: '/admin/drivers'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of the JSM Portal</p>
        </div>

        {user?.role === 'admin' && <ClerkStatusList />}
        {user?.role === 'clerk' && (
          <>
            <ClerkKPIWidget />
            <ClerkTasksWidget />
          </>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                    <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {loading ? '...' : stat.value}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {stat.description}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        <FuelBillingCycleWidget />

        {user?.role === 'admin' && <UpcomingLeavesCard />}

        {/* Headcount by Division */}
        <Card data-testid="headcount-division-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-indigo-500" />
                Headcount by Division
              </CardTitle>
              <CardDescription>
                Total: <span className="font-bold text-foreground">{stats?.total_headcount ?? 0}</span> people
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={openEditDivisions} data-testid="edit-divisions-btn">
              <Pencil className="h-4 w-4 mr-1" /> Edit
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(stats?.divisions || {}).map(([key, count]) => (
                <div key={key} className={`rounded-lg ${DIVISION_COLORS[key] || 'bg-slate-500'} text-white p-3`} data-testid={`division-tile-${key}`}>
                  <div className="text-xs uppercase opacity-90">{DIVISION_LABELS[key] || key.replace(/_/g, ' ')}</div>
                  <div className="text-3xl font-extrabold mt-1">{count}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Edit divisions dialog */}
        <Dialog open={divDialogOpen} onOpenChange={setDivDialogOpen}>
          <DialogContent data-testid="edit-divisions-dialog">
            <DialogHeader>
              <DialogTitle>Edit Headcount by Division</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {Object.entries(divForm).map(([key, val]) => (
                <div key={key} className="flex items-center gap-3">
                  <label className="w-44 text-sm font-medium">{DIVISION_LABELS[key] || key.replace(/_/g, ' ')}</label>
                  <Input
                    type="number"
                    min="0"
                    value={val}
                    onChange={(e) => setDivForm({ ...divForm, [key]: e.target.value })}
                    className="flex-1"
                    data-testid={`division-input-${key}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDivDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveDivisions} disabled={divSaving} data-testid="save-divisions-btn">
                {divSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Alert for pending items */}
        {stats?.pending_registrations > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">Action Required</p>
                  <p className="text-sm text-amber-700">
                    {stats.pending_registrations} new driver registration{stats.pending_registrations !== 1 ? 's' : ''} awaiting review
                  </p>
                </div>
              </div>
              <Link to="/admin/registrations">
                <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-100">
                  Review Now
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Live Fleet Map — every clocked-in staff/driver on OpenStreetMap */}
        <LiveFleetMap />

        {/* Weekly leaderboard strip — top delivery staff this week */}
        <WeeklyLeaderboardStrip />

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="card-interactive">
            <Link to="/admin/statements">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Upload Statements</h3>
                  <p className="text-sm text-muted-foreground">Upload new pay statements for drivers</p>
                </div>
              </CardContent>
            </Link>
          </Card>

          <Card className="card-interactive">
            <Link to="/admin/drivers">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Manage Drivers</h3>
                  <p className="text-sm text-muted-foreground">Create, edit, and manage driver accounts</p>
                </div>
              </CardContent>
            </Link>
          </Card>

          <Card className="card-interactive">
            <Link to="/admin/registrations">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center">
                  <ClipboardList className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Review Registrations</h3>
                  <p className="text-sm text-muted-foreground">Approve or reject new driver applications</p>
                </div>
              </CardContent>
            </Link>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
